"use client";

import { FUN_DESTINATION_ASSET_KEY, type FunActivityResponse } from "@decibeltrade/utils";
import { useEffect, useState } from "react";

import { useAnalytics } from "@/contexts/analytics-context";
import { useFunPayments } from "@/contexts/queries/fun/fun-activity";
import { useXchainDepositsQuery, type XchainDeposit } from "@/contexts/queries/xchain/deposits";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { useAuthState } from "@/hooks/use-auth-state";
import { addressComparisonKey } from "@/utils/address";
import { Logger } from "@/utils/logger";
import { getServerTime } from "@/utils/server-time";
import { MS_PER_MINUTE } from "@/utils/time";

import type { TransferProgressData } from "./transfer-progress-card";
import { useTransferProgress } from "./use-transfer-progress";
import { importedDeposits } from "./xchain-imported-deposits";
import {
  xchainDepositAmount,
  xchainDepositAmountFormatted,
  xchainFeeFormatted,
  xchainFeeUsd,
  xchainStepForStage,
} from "./xchain-stage";

/**
 * The origin recorded against the transfer. Not shown to the user.
 *
 * A delivery does not say which chain funded it — many chains fund one Intents account — so there
 * is nothing here that a user would recognise as where they sent from. NEAR is only the leg that
 * delivered, so the card's copy stays chainless and this feeds the two consumers for which the
 * delivering leg is the right answer: the `origin_chain` analytics dimension, and the static
 * finality estimate under the confirming step.
 */
const XCHAIN_SOURCE_CHAIN = "NEAR";

/**
 * Delivery ids already warned about below, so a refetch does not re-log the same one.
 *
 * Deliberately not the `importedDeposits` ledger: an unclassified delivery may still turn into a
 * real one, and marking it imported would suppress the card when it does.
 */
const warnedUnclassified = new Set<string>();

/** How long a Fun delivery waits for its Fun item to claim it before getting its own card. */
export const FUN_DELIVERY_HOLD_MS = 5 * MS_PER_MINUTE;

/**
 * When a Fun delivery may get its own card; null when a Fun item owns it. Held while a Fun item
 * may still claim it, capped so money is never hidden (the Fun tracker folds in a late claim).
 */
function funDeliveryReleaseAt(
  deposit: XchainDeposit,
  fun: FunActivityResponse | undefined,
): number | null {
  if (fun?.claimedDeliveryIds.includes(deposit.id)) return null;
  if (deposit.destinationAssetKey !== FUN_DESTINATION_ASSET_KEY) return 0;
  const updatedMs = deposit.updatedAt === null ? Number.NaN : Date.parse(deposit.updatedAt);
  if (!Number.isFinite(updatedMs)) return 0;
  const baseReadAsOfMs = fun?.baseReadAsOf ? Date.parse(fun.baseReadAsOf) : Number.NaN;
  if (baseReadAsOfMs >= updatedMs) return 0;
  return updatedMs + FUN_DELIVERY_HOLD_MS;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

type XchainDepositTransfer = Extract<TransferProgressData, { type: "xchain-deposit" }>;

/** Terminal locally: the user already moved the funds into their subaccount. */
function isLocallySettled(step: TransferProgressData["currentStep"]): boolean {
  return step === "completed";
}

/**
 * Whether the delivery has reached a stage nothing can move it out of.
 *
 * Under the collector model this is true of every delivery we ever observe: a withdrawal is not
 * reported until it has settled, so `waiting` and `pending` have no upstream representation. That
 * is exactly why the settled event cannot be left on the step transition alone — there is no
 * non-terminal step for a card to transition out of.
 */
function isTerminalStage(deposit: XchainDeposit): boolean {
  return deposit.stage === "delivered" || deposit.stage === "failed";
}

/**
 * What the server said arrived, for the card to carry.
 *
 * Comes with the delivery rather than being joined against the asset allowlist: the allowlist is
 * keyed on origin rows, and a delivery has no origin to key on.
 */
function deliveredAssetFields(deposit: XchainDeposit): Partial<XchainDepositTransfer> {
  return {
    destinationSymbol: deposit.destinationSymbol,
    destinationFaAddress: deposit.destinationFaAddress,
    destinationDecimals: deposit.destinationDecimals,
    sweepable: deposit.sweepable,
  };
}

/**
 * Mirrors the server's view of cross-chain (NEAR Intents) deposits into transfer progress cards.
 *
 * The funds land in the primary subaccount's fungible store — inside the trading account, but not
 * yet cross margin — so a delivered deposit maps to the `available` step, which is what makes the
 * card render `DepositToExchangeAction` for the move into collateral.
 *
 * One card per *delivery*, not per address: one address funds an Intents account that accrues
 * every deposit the user ever makes, so a card keyed on the address would show the first deposit
 * forever and silently swallow the rest.
 */
export function useXchainDepositTracker(): void {
  const { state, accountAddress, subaccountAddress, ownerWalletAddress } = useAuthState();
  const { primarySubaccountAddress, getAuthHeaders } = useWriteDexSdk();
  const { transfers, addTransfer, updateTransfer } = useTransferProgress();
  const analytics = useAnalytics();
  const { data: fun } = useFunPayments();
  // Bumped when a held Fun delivery's hold runs out, so the effect re-checks it.
  const [holdTick, setHoldTick] = useState(0);

  const enabled = state === "WALLET_CONNECTED" && Boolean(subaccountAddress);

  const { data: deposits } = useXchainDepositsQuery({
    accountAddress,
    getAuthHeaders,
    subaccountAddress,
    enabled,
  });

  useEffect(() => {
    if (!deposits || !accountAddress || !primarySubaccountAddress || !ownerWalletAddress) return;

    const existingById = new Map(
      transfers
        .filter((transfer): transfer is XchainDepositTransfer => transfer.type === "xchain-deposit")
        .map((transfer) => [transfer.deliveryId, transfer]),
    );
    const ledger = importedDeposits(ownerWalletAddress);
    const importedKeys = ledger.read();
    const newlyImported: string[] = [];
    const nowMs = getServerTime();
    let nextReleaseMs = Number.POSITIVE_INFINITY;

    const reportSettled = (deposit: XchainDeposit) => {
      analytics.portfolio.xchainDeposit.settled({
        destinationAssetKey: deposit.destinationAssetKey,
        status: deposit.stage === "delivered" ? "success" : "error",
        stage: deposit.stage,
        amount: xchainDepositAmount(deposit),
        // The raw upstream status. Useless as user-facing copy — it is only ever the bare word
        // `FAILED` — but it is the one thing that would distinguish a new failure mode from the
        // known one on a dashboard.
        ...(deposit.stage === "failed" && deposit.status && { errorCode: deposit.status }),
      });
    };

    for (const deposit of deposits) {
      const step = xchainStepForStage(deposit.stage);
      // Nothing arrived (`waiting`), or the server could not classify what did (`pending`). Either
      // way there is no outcome to report, and a card built from one would have no later
      // transition to wait for.
      if (!step) {
        // `pending` is the catch-all `toDepositStage` uses for a 1Click status it does not map, so
        // it is the only signal that upstream grew a status we do not handle. Hiding the card is
        // right; hiding the fact is not.
        if (deposit.stage === "pending" && !warnedUnclassified.has(deposit.id)) {
          warnedUnclassified.add(deposit.id);
          Logger.warn("[xchain-deposit] Unclassified delivery stage", {
            deliveryId: deposit.id,
            status: deposit.status,
          });
        }
        continue;
      }

      const existing = existingById.get(deposit.id);

      if (existing) {
        if (isLocallySettled(existing.currentStep)) continue;

        const update: Partial<XchainDepositTransfer> = {};
        if (existing.currentStep !== step) {
          update.currentStep = step;
          // Only on the transition, and only into a terminal stage: this effect re-runs on every
          // poll, so anything keyed off the current step alone would fire repeatedly. Dead today
          // — every card is created terminal, see `isTerminalStage` — but it is what covers a
          // delivery first seen mid-flight if 1Click ever reports one.
          if (isTerminalStage(deposit)) reportSettled(deposit);
        }

        // Both or neither: the string is what the sweep converts and the number is what the
        // card renders, so letting them advance separately would show one figure and move
        // another. A positive amount always has a string behind it — the number is derived
        // from it — so this only ever skips the window before any amount is reported.
        const amountFormatted = xchainDepositAmountFormatted(deposit);
        const amount = xchainDepositAmount(deposit);
        if (amountFormatted && amount > 0 && amount !== existing.amount) {
          update.amount = amount;
          update.amountFormatted = amountFormatted;
        }

        if (deposit.destinationTxHash && existing.destinationTxHash !== deposit.destinationTxHash) {
          update.destinationTxHash = deposit.destinationTxHash;
        }

        // Backfill for a card persisted before the delivery carried these. Nothing can fail to
        // report them now, so this only ever fires for a card that predates the field.
        const delivered = deliveredAssetFields(deposit);
        if (
          addressComparisonKey(deposit.destinationFaAddress) !==
          addressComparisonKey(existing.destinationFaAddress)
        ) {
          Object.assign(update, delivered);
        }

        // The fee only exists once the delivery has settled, so a card created before then (or
        // before the server carried this field at all) backfills it here rather than at creation.
        const feeFormatted = xchainFeeFormatted(deposit);
        if (feeFormatted !== undefined && feeFormatted !== existing.feeFormatted) {
          update.feeFormatted = feeFormatted;
        }
        const feeUsd = xchainFeeUsd(deposit);
        if (feeUsd !== undefined && feeUsd !== existing.feeUsd) {
          update.feeUsd = feeUsd;
        }

        // Writing unconditionally would rewrite localStorage on every poll and re-render every
        // card with it.
        if (Object.keys(update).length > 0) updateTransfer(existing.id, update);
        continue;
      }

      // Dismissing a card has to stick: the server keeps reporting a settled delivery forever,
      // so without the ledger the card would come straight back on the next poll.
      if (importedKeys.has(deposit.id)) continue;

      const releaseAt = funDeliveryReleaseAt(deposit, fun);
      if (releaseAt === null) continue;
      if (releaseAt > nowMs) {
        nextReleaseMs = Math.min(nextReleaseMs, releaseAt);
        continue;
      }

      const amountFormatted = xchainDepositAmountFormatted(deposit);
      const feeFormatted = xchainFeeFormatted(deposit);
      const feeUsd = xchainFeeUsd(deposit);

      addTransfer({
        type: "xchain-deposit",
        deliveryId: deposit.id,
        destinationAssetKey: deposit.destinationAssetKey,
        amount: xchainDepositAmount(deposit),
        // The figure the sweep converts to base units. Absent only if the server reported none,
        // which is the same window in which `amount` is 0 and there is nothing to move anyway.
        ...(amountFormatted && { amountFormatted }),
        // Every card here is created terminal (see `isTerminalStage`), so a delivered one already
        // has its fee; a failed one never will.
        ...(feeFormatted !== undefined && { feeFormatted }),
        ...(feeUsd !== undefined && { feeUsd }),
        sourceChain: XCHAIN_SOURCE_CHAIN,
        ...deliveredAssetFields(deposit),
        destinationChain: "Aptos",
        // Both point at the primary subaccount, not the wallet and not the selected subaccount:
        // that is where 1Click delivered the funds, and it is the only account the follow-up
        // move into collateral can operate on.
        destinationAddress: primarySubaccountAddress,
        subaccountToDepositTo: primarySubaccountAddress,
        currentStep: step,
        ...(deposit.destinationTxHash && { destinationTxHash: deposit.destinationTxHash }),
      });

      // The first time we ever see this delivery. Guarded by the same ledger that guards the card,
      // so it fires once per delivery rather than once per poll — but it is per browser, so the
      // same deposit observed first on a second device counts again.
      analytics.portfolio.xchainDeposit.detected({
        destinationAssetKey: deposit.destinationAssetKey,
        status: "initiated",
        stage: deposit.stage,
        amount: xchainDepositAmount(deposit),
      });

      // Fired alongside `detected` rather than only on a later transition, because there is no
      // later transition to wait for: 1Click does not report a withdrawal until it has settled, so
      // the first sighting is already the outcome. Guarded by the same ledger, so it stays once
      // per delivery rather than once per poll.
      if (isTerminalStage(deposit)) reportSettled(deposit);

      newlyImported.push(deposit.id);
    }

    if (newlyImported.length > 0) ledger.add(...newlyImported);

    if (!Number.isFinite(nextReleaseMs)) return;
    const timer = setTimeout(() => setHoldTick((tick) => tick + 1), nextReleaseMs - nowMs);
    return () => clearTimeout(timer);
  }, [
    analytics,
    deposits,
    fun,
    holdTick,
    transfers,
    accountAddress,
    primarySubaccountAddress,
    ownerWalletAddress,
    addTransfer,
    updateTransfer,
  ]);
}
